import React from "react";
import { getAuthToken } from "../lib/auth";
import { isNativeStoreApp } from "../lib/appMode";
import {
  createAndroidPushChannels,
  getFirebaseMessagingPlugin,
  getFirebaseMessagingToken,
  getNativePlatform,
  getNativePushPermission,
  getNativePushPlugin,
  getStoredPushToken,
  openPushDestination,
  registerPushTokenWithServer,
  requestNativePushRegistration,
  unregisterCurrentPushDevice,
} from "../lib/nativePush";

const DISMISS_KEY = "ga_push_prompt_dismissed_at";
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function normalizeNotification(payload) {
  return payload?.notification || payload || {};
}

export default function NativePushManager() {
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [toast, setToast] = React.useState(null);

  const previousAuthRef = React.useRef(getAuthToken());
  const lastRegisteredTokenRef = React.useRef("");

  React.useEffect(() => {
    if (!isNativeStoreApp()) return undefined;

    const platform = getNativePlatform();
    const pushPlugin = getNativePushPlugin();
    const firebasePlugin = getFirebaseMessagingPlugin();

    if (!pushPlugin && !firebasePlugin) return undefined;

    let active = true;
    const handles = [];

    const add = async (plugin, eventName, callback) => {
      if (!plugin?.addListener) return;

      try {
        const handle = await plugin.addListener(eventName, callback);
        if (handle) handles.push(handle);
      } catch (error) {
        console.warn(`[push] ${eventName} listener failed`, error);
      }
    };

    const finishPhoneRegistration = async (token) => {
      const cleanToken = String(token || "").trim();
      if (!cleanToken) return;

      if (lastRegisteredTokenRef.current === cleanToken) return;
      lastRegisteredTokenRef.current = cleanToken;

      try {
        await registerPushTokenWithServer(cleanToken);

        if (active) {
          setShowPrompt(false);
          setMessage("");
        }
      } catch (error) {
        lastRegisteredTokenRef.current = "";
        console.warn("[push] phone registration failed", error);

        if (active) {
          setMessage(
            error?.message || "Could not finish phone notification setup."
          );
        }
      }
    };

    const showForegroundNotification = (payload) => {
      if (!active) return;

      const notification = normalizeNotification(payload);

      setToast({
        title: notification?.title || "GAStreaming",
        body: notification?.body || "New activity",
        notification,
      });

      window.dispatchEvent(new Event("ga:notifications-refresh"));
    };

    const performNotificationAction = (payload) => {
      const notification = normalizeNotification(payload);

      setToast(null);
      openPushDestination(notification);
      window.dispatchEvent(new Event("ga:notifications-refresh"));
    };

    add(pushPlugin, "registration", async (event) => {
      try {
        let token = String(event?.value || event?.token || "").trim();

        if (platform === "ios" && firebasePlugin?.getToken) {
          const fcmToken = await getFirebaseMessagingToken();
          if (fcmToken) token = fcmToken;
        }

        await finishPhoneRegistration(token);
      } catch (error) {
        console.warn("[push] registration token conversion failed", error);

        if (active) {
          setMessage(
            error?.message || "Could not finish iPhone notification setup."
          );
        }
      }
    });

    add(pushPlugin, "registrationError", (error) => {
      console.warn("[push] native registration error", error);

      if (active) {
        setMessage("This phone could not register for notifications yet.");
      }
    });

    if (platform === "android" || !firebasePlugin?.addListener) {
      add(
        pushPlugin,
        "pushNotificationReceived",
        showForegroundNotification
      );
      add(
        pushPlugin,
        "pushNotificationActionPerformed",
        performNotificationAction
      );
    }

    if (platform === "ios" && firebasePlugin?.addListener) {
      add(firebasePlugin, "tokenReceived", async (event) => {
        await finishPhoneRegistration(event?.token || event?.value || "");
      });
      add(
        firebasePlugin,
        "notificationReceived",
        showForegroundNotification
      );
      add(
        firebasePlugin,
        "notificationActionPerformed",
        performNotificationAction
      );
    }

    const syncForAuth = async () => {
      const authToken = getAuthToken();
      const previousToken = previousAuthRef.current;
      previousAuthRef.current = authToken;

      if (!authToken && previousToken) {
        await unregisterCurrentPushDevice(previousToken).catch(() => {});
        setShowPrompt(false);
        return;
      }

      if (!authToken) return;

      const permission = await getNativePushPermission();

      if (permission === "granted") {
        await createAndroidPushChannels();

        const storedToken = getStoredPushToken();

        if (storedToken) {
          await registerPushTokenWithServer(
            storedToken,
            authToken
          ).catch(() => {});
        }

        if (platform === "ios" && firebasePlugin?.getToken) {
          try {
            const currentToken = await getFirebaseMessagingToken();
            if (currentToken) await finishPhoneRegistration(currentToken);
          } catch (error) {
            console.warn("[push] iOS token refresh failed", error);
          }
        }

        if (pushPlugin?.register) {
          await pushPlugin.register().catch(() => {});
        } else if (firebasePlugin?.getToken) {
          try {
            const currentToken = await getFirebaseMessagingToken();
            if (currentToken) await finishPhoneRegistration(currentToken);
          } catch (error) {
            console.warn("[push] Firebase registration failed", error);
          }
        }

        return;
      }

      if (
        permission === "prompt" ||
        permission === "prompt-with-rationale"
      ) {
        const dismissedAt = Number(
          localStorage.getItem(DISMISS_KEY) || 0
        );

        if (!dismissedAt || Date.now() - dismissedAt > WEEK_MS) {
          window.setTimeout(
            () => active && setShowPrompt(true),
            900
          );
        }
      }
    };

    const onEnableRequest = () => {
      setShowPrompt(true);
      setMessage("");
    };

    window.addEventListener("auth:changed", syncForAuth);
    window.addEventListener("ga:push-enable-request", onEnableRequest);

    syncForAuth().catch((error) =>
      console.warn("[push] auth sync failed", error)
    );

    return () => {
      active = false;

      window.removeEventListener("auth:changed", syncForAuth);
      window.removeEventListener(
        "ga:push-enable-request",
        onEnableRequest
      );

      for (const handle of handles) {
        try {
          const result = handle?.remove?.();
          result?.catch?.(() => {});
        } catch {}
      }
    };
  }, []);

  React.useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 6500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!isNativeStoreApp()) return null;

  const enable = async () => {
    setBusy(true);
    setMessage("");

    try {
      await requestNativePushRegistration();
      setShowPrompt(false);
      window.dispatchEvent(new Event("ga:push-settings-refresh"));
    } catch (error) {
      setMessage(
        error?.code === "permission_denied"
          ? "Notifications are off for GAStreaming. You can enable them later in your phone settings."
          : error?.message || "Could not enable notifications."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {showPrompt ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enable GAStreaming notifications"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "grid",
            placeItems: "center",
            padding: 18,
            background: "rgba(0,0,0,.72)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              width: "min(430px, 100%)",
              borderRadius: 22,
              padding: 22,
              color: "white",
              background:
                "linear-gradient(145deg, rgba(35,8,16,.98), rgba(8,10,16,.98))",
              border: "1px solid rgba(255,70,105,.38)",
              boxShadow: "0 24px 80px rgba(0,0,0,.65)",
            }}
          >
            <div style={{ fontSize: 30, marginBottom: 8 }}>🔔</div>
            <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>
              Stay active with GAStreaming
            </h2>
            <p style={{ margin: 0, opacity: 0.84, lineHeight: 1.5 }}>
              Get alerts when an actual GAStreaming streamer goes live,
              when someone mentions your username, and when tournaments
              or important site updates are posted.
            </p>

            {message ? (
              <div
                style={{
                  marginTop: 12,
                  color: "#ffb7c3",
                  fontSize: 13,
                }}
              >
                {message}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 20,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={enable}
                disabled={busy}
                style={{
                  flex: 1,
                  minWidth: 150,
                  border: 0,
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontWeight: 950,
                  cursor: busy ? "wait" : "pointer",
                  color: "white",
                  background:
                    "linear-gradient(135deg, #ff1744, #b00020)",
                }}
              >
                {busy ? "Enabling…" : "Enable notifications"}
              </button>

              <button
                type="button"
                onClick={() => {
                  localStorage.setItem(
                    DISMISS_KEY,
                    String(Date.now())
                  );
                  setShowPrompt(false);
                }}
                style={{
                  borderRadius: 14,
                  padding: "12px 16px",
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "rgba(255,255,255,.06)",
                  color: "white",
                  fontWeight: 850,
                  cursor: "pointer",
                }}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <button
          type="button"
          onClick={() => openPushDestination(toast.notification)}
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            left: 12,
            right: 12,
            zIndex: 99999,
            margin: "0 auto",
            maxWidth: 520,
            textAlign: "left",
            borderRadius: 16,
            padding: "12px 14px",
            color: "white",
            border: "1px solid rgba(255,255,255,.2)",
            background: "rgba(12,12,18,.96)",
            boxShadow: "0 14px 50px rgba(0,0,0,.55)",
            cursor: "pointer",
          }}
        >
          <div style={{ fontWeight: 950 }}>{toast.title}</div>
          <div
            style={{
              marginTop: 3,
              opacity: 0.82,
              fontSize: 13,
            }}
          >
            {toast.body}
          </div>
        </button>
      ) : null}
    </>
  );
}
